import { DeleteEventModal } from '@/components/ui/DeleteEventModal';
import { FinishEventModal } from '@/components/ui/FinishEventModal';
import { RepublishEventModal } from '@/components/ui/RepublishEventModal';
import { SkeletonEventsList } from '@/components/ui/Skeleton';
import { EventCard } from '@/features/events/components/EventCard';
import EventOrganizersModal from '@/features/events/components/EventOrganizersModal';
import { useToast } from '@/hooks/useToast';
import { api } from '@/lib/api';
import { hasEventEnded } from '@/lib/eventHelpers';
import { formatEventDate } from '@/lib/utils';
import { useAppSelector } from '@/store/hooks';
import type { Event } from '@eventflow/shared-types';
import { Plus, Ticket } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function MyEventsPage() {
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [selectedEventName, setSelectedEventName] = useState('');
  const { success, error: showError } = useToast();
  const { organizer } = useAppSelector((state) => state.auth);

  // Filtros rápidos
  const [activeFilter, setActiveFilter] = useState<'active' | 'archived' | 'past'>('active');

  // Estado para el modal de eliminación
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Estado para el modal de finalización
  const [finishModalOpen, setFinishModalOpen] = useState(false);
  const [eventToFinish, setEventToFinish] = useState<Event | null>(null);
  const [isFinishing, setIsFinishing] = useState(false);



  // Estado para el modal de republicar
  const [republishModalOpen, setRepublishModalOpen] = useState(false);
  const [eventToRepublish, setEventToRepublish] = useState<Event | null>(null);
  const [isRepublishing, setIsRepublishing] = useState(false);

  const loadEvents = async () => {
    try {
      setIsLoading(true);
      setError('');
      const eventsData = await api.get<Event[]>('/events');

      setEvents(eventsData);
    } catch (err) {
      console.error('Error loading events:', err);
      const errorMessage = err instanceof Error ? err.message : 'Error al cargar eventos';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Abrir modal de confirmación de eliminación
  const handleDelete = (eventId: string, _eventName: string) => {
    const event = events.find((e) => e.id === eventId);
    if (event) {
      setEventToDelete(event);
      setDeleteModalOpen(true);
    }
  };

  // Confirmar eliminación
  const confirmDelete = async () => {
    if (!eventToDelete) return;

    try {
      setIsDeleting(true);
      await api.delete(`/events/${eventToDelete.id}`);
      setEvents(events.filter((e) => e.id !== eventToDelete.id));
      success('Evento eliminado exitosamente');
      setDeleteModalOpen(false);
      setEventToDelete(null);
    } catch (err) {
      console.error('Error deleting event:', err);
      showError(
        'Error al eliminar evento: ' + (err instanceof Error ? err.message : 'Error desconocido')
      );
    } finally {
      setIsDeleting(false);
    }
  };

  // Abrir modal de confirmación de finalización
  const handleFinish = (eventId: string, _eventName: string) => {
    const event = events.find((e) => e.id === eventId);
    if (event) {
      // Validar que el evento no esté ya finalizado
      if (event.status === 'finished') {
        showError('Este evento ya está finalizado');
        return;
      }
      setEventToFinish(event);
      setFinishModalOpen(true);
    }
  };

  // Confirmar finalización
  const confirmFinish = async () => {
    if (!eventToFinish) return;

    try {
      setIsFinishing(true);
      await api.post(`/events/${eventToFinish.id}/finish`, {});
      // Actualizar el evento en el estado local
      setEvents((prev) =>
        prev.map((e) =>
          e.id === eventToFinish.id ? { ...e, status: 'finished', is_published: false } : e
        )
      );
      success('Evento finalizado exitosamente');
      setFinishModalOpen(false);
      setEventToFinish(null);
    } catch (err) {
      console.error('Error finishing event:', err);
      showError(
        'Error al finalizar evento: ' + (err instanceof Error ? err.message : 'Error desconocido')
      );
    } finally {
      setIsFinishing(false);
    }
  };



  // Abrir modal de republicar
  const handleRepublish = (eventId: string, _eventName: string) => {
    const event = events.find((e) => e.id === eventId);
    if (event) {
      setEventToRepublish(event);
      setRepublishModalOpen(true);
    }
  };

  // Confirmar republicar
  const confirmRepublish = async (data: { event_date: string; event_start_time: string; event_end_time: string }) => {
    if (!eventToRepublish) return;

    try {
      setIsRepublishing(true);
      await api.post(`/events/${eventToRepublish.id}/republish`, data);
      // Actualizar el evento en el estado local
      setEvents((prev) =>
        prev.map((e) =>
          e.id === eventToRepublish.id
            ? {
              ...e,
              event_date: data.event_date,
              event_start_time: data.event_start_time,
              event_end_time: data.event_end_time,
              status: 'published',
              is_published: true,
            }
            : e
        )
      );
      success('Evento vuelto a publicar exitosamente con nuevas fechas');
      setRepublishModalOpen(false);
      setEventToRepublish(null);
    } catch (err) {
      console.error('Error republishing event:', err);
      showError(
        'Error al volver a publicar evento: ' + (err instanceof Error ? err.message : 'Error desconocido')
      );
    } finally {
      setIsRepublishing(false);
    }
  };

  const handleTogglePublish = async (
    eventId: string,
    currentPublished: boolean,
    eventName: string
  ) => {
    try {
      await api.post(`/events/${eventId}/publish`, {
        isPublished: !currentPublished,
      });
      setEvents((prev) =>
        prev.map((e) =>
          e.id === eventId
            ? {
              ...e,
              is_published: !currentPublished,
              status: !currentPublished ? 'published' : 'draft',
            }
            : e
        )
      );
      success(
        `Evento "${eventName}" ${!currentPublished ? 'publicado' : 'despublicado'} exitosamente`
      );
    } catch (err) {
      console.error('Error toggling publish state:', err);
      showError(
        'Error al cambiar estado de publicación: ' +
        (err instanceof Error ? err.message : 'Error desconocido')
      );
    }
  };

  // Archivar evento
  const handleArchive = async (eventId: string, eventName: string) => {
    const event = events.find((e) => e.id === eventId);
    if (!event) return;

    // Validar que el evento esté finalizado
    if (event.status !== 'finished') {
      showError('Solo se pueden archivar eventos finalizados');
      return;
    }

    try {
      await api.post(`/events/${eventId}/archive`, {});
      // Actualizar el evento en el estado local
      setEvents((prev) =>
        prev.map((e) =>
          e.id === eventId ? { ...e, status: 'archived' } : e
        )
      );
      success(`Evento "${eventName}" archivado exitosamente`);
    } catch (err) {
      console.error('Error archiving event:', err);
      showError(
        'Error al archivar evento: ' + (err instanceof Error ? err.message : 'Error desconocido')
      );
    }
  };

  if (isLoading && events.length === 0) {
    return <SkeletonEventsList />;
  }

  if (error && events.length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  // Filtrar eventos según el filtro activo
  // IMPORTANTE: Usamos `effective_status` del backend como Single Source of Truth
  // El backend calcula este campo considerando tanto el status de BD como la fecha del evento
  const getEffectiveStatus = (event: any): string => {
    // Si el backend envía effective_status, usarlo
    if (event.effective_status) {
      return event.effective_status;
    }
    // Fallback: calcular localmente (para compatibilidad)
    const isFinishedTime = hasEventEnded({
      event_date: event.event_date,
      event_end_time: event.event_end_time || null,
    });
    if (event.status === 'archived') return 'archived';
    if (event.status === 'finished') return 'finished';
    if (isFinishedTime && (event.status === 'published' || event.status === 'draft')) {
      return 'finished';
    }
    return event.status;
  };

  const filteredEvents = events.filter((event) => {
    const effectiveStatus = getEffectiveStatus(event);

    if (activeFilter === 'active') {
      // Vigentes: published o draft (que NO han expirado)
      return effectiveStatus === 'published' || effectiveStatus === 'draft';
    } else if (activeFilter === 'archived') {
      // Archivados
      return effectiveStatus === 'archived';
    } else if (activeFilter === 'past') {
      // Finalizados
      return effectiveStatus === 'finished';
    }
    return true;
  });

  // Contar eventos por categoría usando effective_status
  const activeCount = events.filter((e) => {
    const eff = getEffectiveStatus(e);
    return eff === 'published' || eff === 'draft';
  }).length;

  const archivedCount = events.filter((e) => getEffectiveStatus(e) === 'archived').length;

  const pastCount = events.filter((e) => getEffectiveStatus(e) === 'finished').length;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Mis Eventos</h1>
        <Link
          to="/dashboard/events/new"
          className="btn-primary text-center sm:w-auto flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" /> Crear Evento
        </Link>
      </div>

      {/* Filtros Rápidos */}
      <div className="mb-6 flex gap-2 overflow-x-auto pb-2">
        <button
          onClick={() => setActiveFilter('active')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeFilter === 'active'
            ? 'bg-blue-500 text-white shadow-sm'
            : 'bg-white text-gray-700 border border-gray-200 hover:border-blue-300 hover:text-blue-600'
            }`}
        >
          Vigentes ({activeCount})
        </button>
        <button
          onClick={() => setActiveFilter('archived')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeFilter === 'archived'
            ? 'bg-blue-500 text-white shadow-sm'
            : 'bg-white text-gray-700 border border-gray-200 hover:border-blue-300 hover:text-blue-600'
            }`}
        >
          Archivados ({archivedCount})
        </button>
        <button
          onClick={() => setActiveFilter('past')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeFilter === 'past'
            ? 'bg-blue-500 text-white shadow-sm'
            : 'bg-white text-gray-700 border border-gray-200 hover:border-blue-300 hover:text-blue-600'
            }`}
        >
          Finalizados ({pastCount})
        </button>
      </div>

      {filteredEvents.length === 0 ? (
        <div className="card text-center py-12">
          <div className="text-6xl mb-4 flex justify-center">
            <Ticket className="w-16 h-16 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">
            {activeFilter === 'active' && 'No tienes eventos vigentes'}
            {activeFilter === 'archived' && 'No tienes eventos archivados'}
            {activeFilter === 'past' && 'No tienes eventos finalizados'}
          </h2>
          <p className="text-gray-600 mb-6">
            {activeFilter === 'active' && 'Crea tu primer evento y empieza a vender boletos con asientos numerados'}
            {activeFilter === 'archived' && 'Los eventos archivados aparecerán aquí'}
            {activeFilter === 'past' && 'Los eventos finalizados aparecerán aquí'}
          </p>
          {activeFilter === 'active' && (
            <Link to="/dashboard/events/new" className="btn-primary inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Crear Mi Primer Evento
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredEvents.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              onTogglePublish={handleTogglePublish}
              onDelete={handleDelete}
              onFinish={handleFinish}
              onRepublish={handleRepublish}
              onArchive={handleArchive}
              onManageTeam={(id, name) => {
                setSelectedEventId(id);
                setSelectedEventName(name);
              }}
            />
          ))}
        </div>
      )}

      {/* Modal de gestión de organizadores */}
      {selectedEventId && (
        <EventOrganizersModal
          eventId={selectedEventId}
          eventName={selectedEventName}
          isCreator={
            organizer?.id === events.find((e) => e.id === selectedEventId)?.organizer_id || false
          }
          readOnly={(() => {
            const selectedEvent = events.find((e) => e.id === selectedEventId);
            if (!selectedEvent) return false;
            // Usar effective_status si disponible, sino status
            const status = (selectedEvent as any).effective_status || selectedEvent.status;
            return status === 'finished' || status === 'archived';
          })()}
          onClose={() => {
            setSelectedEventId(null);
            setSelectedEventName('');
          }}
          onUpdate={(args) => {
            if (!args?.eventId) return;
            setEvents((prev) =>
              prev.map((e) =>
                e.id === args.eventId
                  ? {
                      ...e,
                      organizers: args.organizers,
                    }
                  : e
              )
            );
          }}
        />
      )}

      {/* Modal de confirmación de eliminación */}
      <DeleteEventModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setEventToDelete(null);
        }}
        onConfirm={confirmDelete}
        eventName={eventToDelete?.name || ''}
        eventDate={eventToDelete ? formatEventDate(eventToDelete.event_date) : ''}
        eventVenue={eventToDelete?.venue_name}
        isDeleting={isDeleting}
      />

      {/* Modal de confirmación de finalización */}
      <FinishEventModal
        isOpen={finishModalOpen}
        onClose={() => {
          setFinishModalOpen(false);
          setEventToFinish(null);
        }}
        onConfirm={confirmFinish}
        eventName={eventToFinish?.name || ''}
        eventDate={eventToFinish ? formatEventDate(eventToFinish.event_date) : ''}
        eventVenue={eventToFinish?.venue_name}
        isFinishing={isFinishing}
      />



      {/* Modal de republicar */}
      <RepublishEventModal
        isOpen={republishModalOpen}
        onClose={() => {
          setRepublishModalOpen(false);
          setEventToRepublish(null);
        }}
        onConfirm={confirmRepublish}
        eventName={eventToRepublish?.name || ''}
        currentDate={eventToRepublish ? formatEventDate(eventToRepublish.event_date) : ''}
        currentStartTime={eventToRepublish?.event_start_time || ''}
        currentEndTime={eventToRepublish?.event_end_time || ''}
        isRepublishing={isRepublishing}
      />
    </div>
  );
}
